import type { MatrixClient, Room } from 'matrix-js-sdk';
import { ClientEvent, RoomEvent, RoomMemberEvent, RoomStateEvent } from 'matrix-js-sdk';

type Listener = () => void;

/**
 * Externer Store für Matrix Rooms.
 * Implementiert das Store-Pattern für useSyncExternalStore.
 */
class RoomsStore {
  private listeners = new Set<Listener>();
  private client: MatrixClient | null = null;
  private rooms: Room[] = [];
  private cachedSnapshot: Room[] | null = null;

  /**
   * Setzt den Matrix Client und registriert Event-Listener.
   */
  setClient(client: MatrixClient | null): void {
    // Cleanup old client
    if (this.client) {
      this.client.removeListener(ClientEvent.Room, this.handleRoomsChange);
      this.client.removeListener(ClientEvent.DeleteRoom, this.handleRoomsChange);
      this.client.removeListener(RoomEvent.Timeline, this.handleRoomsChange);
      this.client.removeListener(RoomEvent.Name, this.handleRoomsChange);
      this.client.removeListener(RoomEvent.Receipt, this.handleRoomsChange);
      this.client.removeListener(RoomEvent.MyMembership, this.handleRoomsChange);
      this.client.removeListener(RoomStateEvent.Update, this.handleRoomsChange);
      this.client.removeListener(RoomStateEvent.Events, this.handleRoomsChange);
      this.client.removeListener(RoomMemberEvent.Membership, this.handleRoomsChange);
    }

    this.client = client;
    this.rooms = [];
    this.cachedSnapshot = null; // Invalidiere Cache bei Client-Wechsel

    // Setup new client
    if (this.client) {
      this.rooms = this.client.getRooms().sort((a, b) => {
        const la = a.getLastActiveTimestamp();
        const lb = b.getLastActiveTimestamp();
        return lb - la;
      });

      // Client-Level Events
      this.client.on(ClientEvent.Room, this.handleRoomsChange);
      this.client.on(ClientEvent.DeleteRoom, this.handleRoomsChange);

      // Room-Level Events für Reaktivität bei Änderungen
      this.client.on(RoomEvent.Timeline, this.handleRoomsChange);
      this.client.on(RoomEvent.Name, this.handleRoomsChange);
      this.client.on(RoomEvent.Receipt, this.handleRoomsChange);
      this.client.on(RoomEvent.MyMembership, this.handleRoomsChange);
      this.client.on(RoomStateEvent.Update, this.handleRoomsChange);
      this.client.on(RoomStateEvent.Events, this.handleRoomsChange);
      this.client.on(RoomMemberEvent.Membership, this.handleRoomsChange);
    }

    this.emitChange();
  }

  /**
   * Handler für Änderungen der Rooms.
   * Verwendet Microtask-Queue für effizientes Batching.
   */
  private pendingUpdate = false;

  private handleRoomsChange = (): void => {
    // Batch: Nur ein Update pro Microtask-Zyklus
    if (this.pendingUpdate) return;

    this.pendingUpdate = true;
    queueMicrotask(() => {
      this.pendingUpdate = false;

      if (!this.client) {
        if (this.rooms.length > 0) {
          this.rooms = [];
          this.emitChange();
        }
        return;
      }

      const newRooms = this.client.getRooms().sort((a, b) => {
        const la = a.getLastActiveTimestamp();
        const lb = b.getLastActiveTimestamp();
        return lb - la;
      });

      // Immer neues Array für React-Referenzgleichheit
      // So werden alle Änderungen (inkl. Notification-Counts) erkannt
      this.rooms = [...newRooms];
      this.cachedSnapshot = null; // Invalidiere Cache bei Room-Änderungen
      this.emitChange();
    });
  };

  /**
   * Gibt den aktuellen Snapshot zurück.
   * Cached das Ergebnis um unnötige Re-Renders zu vermeiden.
   */
  getSnapshot = (): Room[] => {
    // Gebe gecachten Snapshot zurück wenn verfügbar
    if (this.cachedSnapshot !== null) {
      return this.cachedSnapshot;
    }

    // Berechne neuen Snapshot
    this.cachedSnapshot = [...this.rooms];

    return this.cachedSnapshot;
  };

  /**
   * Subscribe-Funktion für useSyncExternalStore.
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Benachrichtigt alle Listener über Änderungen.
   */
  private emitChange(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Gibt den aktuellen Client zurück.
   */
  getClient(): MatrixClient | null {
    return this.client;
  }
}

// Singleton-Instanz
export const roomsStore = new RoomsStore();
